import React from 'react';
import {Link, useParams} from 'react-router-dom';
import {Helmet} from 'react-helmet-async';
import {useQuery, gql} from '@apollo/client';

import Error from '../../component/error/Error';
import Loading from '../../component/loading/Loading';
import {PRODUCT_FRAGMENT} from '../../component/productPreview/ProductPreview';
import ProductsList from '../../component/productsList/ProductsList';
import {USER_FRAGMENT} from '../../component/userPreview/UserPreview';
import {collectionUrl, webappURL} from '../../util/url';
import NotFound from '../notFound/NotFound';

import {PRODUCT_LIST_ITEM_FRAGMENT} from '../../query/productFragments';

import CollectionOpenGraph from './CollectionOpenGraph';
import CollectionTwitterCard from './CollectionTwitterCard';

import './Collection.css';
import PropTypes from 'prop-types';
import {isWithinInt32Range} from "../../util/util";
import CollectionExtraInfo from "./components/CollectionExtraInfo";
import CollectionHero from "./components/CollectionHero";
import {breadcrumbSchemaOrg, collectionSchemaOrg, safeJsonLd} from "../../util/schemaOrg";

// GraphQL queries
const GET_COLLECTION = gql`
    query GetCollection($collectionId: Int!) {
        getCollection(collection_id: $collectionId) {
            id
            title
            subtitle
            sub_header
            description
            resizedBigImage
            resizedSmallImage
            link
            featured_from
            featured_until
            updatedAt
            image_caption
            aspect_ratio
            extra_info
            seller {
                id
                user {
                   ...UserFragment
                   username
               }
            }
        }
    }
    ${USER_FRAGMENT}
`;
const GET_COLLECTION_PRODUCTS = gql`
    query GetCollectionProducts($collectionId: Int!, $offset: Int, $limit: Int) {
        getCollectionProducts(collection_id: $collectionId, offset: $offset, limit: $limit) {
            ...ProductFragment
            ...ProductListItemFragment
        }
    }
    ${PRODUCT_FRAGMENT}
    ${PRODUCT_LIST_ITEM_FRAGMENT}
`;

/**
 * Category collection component.
 * @param hostname
 * @param userAgent
 */
export default function Collection({ hostname, userAgent }) {
    const { collectionId: encodedCollectionId } = useParams();
    const collectionId = parseInt(encodedCollectionId, 36);
    const isValidCollectionId = isWithinInt32Range(collectionId);

    // fetch collection details
    const { loading, error, data } = useQuery(
        GET_COLLECTION,
        {
            variables: {
                collectionId: isValidCollectionId ? collectionId : 0
            },
            skip: !isValidCollectionId,
        }
    );

    if (!isValidCollectionId) {
        return <NotFound />;
    }

    if (loading) {
        return <Loading />;
    } else if (error) {
        return <Error error={error} />;
    }

    // meta tag
    const metaTitle = `${data.getCollection.title} | 후루츠패밀리`;

    return (
        <section className="Collection">
            <Helmet>
                <title>{metaTitle}</title>
                <meta name="description" content={data.getCollection.description} />
                <link rel="canonical" href={webappURL(hostname) + collectionUrl(data.getCollection)} />
                <link rel="alternate" type="application/atom+xml"
                    href={`${collectionUrl(data.getCollection)}/feed.atom`} />
                <script type="application/ld+json">
                    {safeJsonLd([
                        collectionSchemaOrg(data.getCollection, webappURL(hostname)),
                        breadcrumbSchemaOrg([
                            { name: "Home", url: webappURL(hostname) + "/" },
                            { name: "Collections", url: webappURL(hostname) + "/collections" },
                            { name: data.getCollection.title, url: webappURL(hostname) + collectionUrl(data.getCollection) }
                        ])
                    ])}
                </script>
            </Helmet>
            <CollectionOpenGraph collection={data.getCollection} title={metaTitle} hostname={hostname} />
            <CollectionTwitterCard collection={data.getCollection} title={metaTitle} />

            <div className="Collection-breadcrumb-wrapper">
                <nav className="Collection-breadcrumb" aria-label="breadcrumb">
                    <ol className="Collection-breadcrumb-list" itemScope itemType="https://schema.org/BreadcrumbList">
                        <li className="Collection-breadcrumb-item" itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                            <Link to="/" className="Collection-breadcrumb-link" itemProp="item">
                                <span itemProp="name">Home</span>
                            </Link>
                            <meta itemProp="position" content="1" />
                        </li>
                        <li className="Collection-breadcrumb-separator" aria-hidden="true">/</li>
                        <li className="Collection-breadcrumb-item" itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                            <Link to="/collections" className="Collection-breadcrumb-link" itemProp="item">
                                <span itemProp="name">Collections</span>
                            </Link>
                            <meta itemProp="position" content="2" />
                        </li>
                        <li className="Collection-breadcrumb-separator" aria-hidden="true">/</li>
                        <li className="Collection-breadcrumb-item" itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                            <span className="Collection-breadcrumb-current" itemProp="name" aria-current="page">
                                {data.getCollection.title}
                            </span>
                            <meta itemProp="position" content="3" />
                        </li>
                    </ol>
                </nav>
            </div>

            <CollectionHero collection={data.getCollection} />

            {data.getCollection.description && (
                <section className="Collection-intro">
                    <div className="Collection-intro-inner">
                        <p className="Collection-intro-text">
                            {data.getCollection.description}
                        </p>
                    </div>
                </section>
            )}

            {data.getCollection.extra_info && (
                <div className="Collection-extra-info-container">
                    {data.getCollection.extra_info.map((item, index) => (
                        <div key={`collection_extrainfo_${index}`}>
                            <CollectionExtraInfo extraInfo={item} userAgent={userAgent} />
                        </div>
                    ))}
                </div>
            )}

            <div className="Collection-products">
                <ProductsList itemsProperty="getCollectionProducts"
                    isEmptyMsg={false}
                    query={GET_COLLECTION_PRODUCTS}
                    variables={{
                        collectionId
                    }} />
            </div>
        </section>
    );
}
Collection.propTypes = {
    hostname: PropTypes.string.isRequired,
    userAgent: PropTypes.string.isRequired,
};
